import { CurrencyPipe, formatDate } from "@angular/common";
import { inject, Injectable } from "@angular/core";
import { AnnualStatementPageFilter, SigningStatus } from "@models/annual-statements/annual-statement-enums";
import { AnnualStatementRecord } from "@models/annual-statements/annual-statement-record";
import { ExportTypeEnum } from "@models/common/export-type.enum";
import { Tag } from "@models/common/tag";
import { ColumnWithExportName } from "@models/grid/column-with-export-name";
import { FilterParams } from "@models/grid/grid-filter";
import { ExportEntityListModalComponent } from "@shared/components/commons/export-company-list-modal/export-entity-list-modal.component";
import { ACNPipe } from "@shared/pipes/acnPipe";
import { excelReportTitleStyles, excelTitleForReport } from "@shared/utils/excel-title-for-report";
import { ColDef, CsvExportParams, ExcelExportParams, ExcelRow, ExcelStyle, GridApi, ProcessCellForExportParams, ProcessHeaderForExportParams } from "ag-grid-community";
import { deepClone } from "fast-json-patch";
import { take, tap } from "rxjs";
import { ModalFormsService } from "src/app/forms/services/modal-forms.service";

@Injectable()
export class AnnualStatementsExportService {
  modalFormsService = inject(ModalFormsService);
  currencyPipe = inject(CurrencyPipe);

  readonly excelStyles: ExcelStyle[] = excelReportTitleStyles;
  static exportColumnNames: Record<string, string> = {
    'companyName': 'Company Name',
    'acn': 'ACN',
  };

  exportAnnualList(
    gridApi: GridApi,
    colDefs: ColDef[],
    checkedAnnualStatements: AnnualStatementRecord[],
    totalRows: number,
    isBulkExport: boolean,
    exportType: ExportTypeEnum,
    sheetName: string
  ): void {
    const previousPaginationPageSize = gridApi.paginationGetPageSize();
    gridApi.updateGridOptions({ paginationPageSize: totalRows });
    const modalRef = this.modalFormsService.openModal(ExportEntityListModalComponent, { modalDialogClass: 'export-company-list' });
    const componentInstance = modalRef.componentInstance as ExportEntityListModalComponent;

    componentInstance.title = 'Export Annual Statements';
    componentInstance.subTitle = 'annual statements selected';
    componentInstance.colDefs = deepClone(colDefs) as ColumnWithExportName[];
    componentInstance.numberOfCompanies = isBulkExport ? checkedAnnualStatements.length : totalRows;
    componentInstance.exportType = exportType;
    componentInstance.confirm
      .pipe(
        tap(() => gridApi.updateGridOptions({ paginationPageSize: totalRows })),
        take(1),
      )
      .subscribe((columnForExport: string[]) => {
        const nameIndex = columnForExport.findIndex(field => field === 'companyName');
        if (nameIndex >= 0) {
          columnForExport.splice(nameIndex + 1, 0, 'acn');
        }

        const numberColumnForExport = columnForExport.length - 1;
        const selectedStatusFilterOptions = this.getSelectedFilterOptions(gridApi, 'status');
        const fileName = `${ selectedStatusFilterOptions } ${ sheetName } ${ formatDate(new Date(), 'dd-MM-yyyy', 'en-US') }`.trim();

        if (exportType === ExportTypeEnum.EXCEL) {
          const params: ExcelExportParams = isBulkExport ? {
            columnKeys: columnForExport,
            onlySelected: true
          } : { columnKeys: columnForExport };
          const exportParamsXls = this.exportParamsXls(numberColumnForExport, sheetName) as ExcelExportParams;
          gridApi.exportDataAsExcel({ ...exportParamsXls, ...params, fileName });
        } else if (exportType === ExportTypeEnum.CSV) {
          const params: CsvExportParams = isBulkExport ? {
            columnKeys: columnForExport,
            onlySelected: true
          } : { columnKeys: columnForExport };
          gridApi.exportDataAsCsv({ ...params, fileName });
        }

        gridApi.updateGridOptions({ paginationPageSize: previousPaginationPageSize });
      });

    componentInstance.resetPagination
      .pipe(
        tap(() => gridApi.updateGridOptions({ paginationPageSize: previousPaginationPageSize })),
        take(1)
      )
      .subscribe();
  }

  getSelectedFilterOptions(gridApi: GridApi, columnId: string): string {
    if (!gridApi) { return ''; }
    const filterParams = gridApi.getColumn(columnId)?.getColDef().filterParams as FilterParams;
    const filterOptions = filterParams?.filterOptions ?? [];
    const selectedFilterOptions = filterOptions.filter(o => o.active).map(o => o.label).join(', ');
    return selectedFilterOptions;
  }

  getExcelTitleRows: (numberColumnForExport: number) => ExcelRow[] = (numberColumnForExport) => {
    return excelTitleForReport(numberColumnForExport, 'Annual Statements Report');
  }

  exportParamsXls(numberColumnForExport: number = 0, sheetName: string = 'Annual Statements'): ExcelExportParams {
    return {
      ...this.exportParams(sheetName),
      prependContent: this.getExcelTitleRows(numberColumnForExport)
    } as ExcelExportParams;
  }

  exportParams(sheetName: string) {
    const currencyPipe = this.currencyPipe;

    return {
      processCellCallback(params: ProcessCellForExportParams): string {
        const value: unknown = params.value;
        const annualData = params.node?.data as AnnualStatementRecord;

        if (params.column.getColId() === 'companyName') {
          return annualData.companyName;
        } if (params.column.getColId() === 'acn') {
          return ACNPipe.formatValue(annualData.acn);
        } if (params.column.getColId() === 'status') {
          return AnnualStatementPageFilter[ annualData.status ];
        } if (params.column.getColId() === 'isPaid') {
          return value ? "Paid" : "Unpaid";
        } if (params.column.getColId() === 'reviewDate') {
          return annualData.reviewDate == null ? '' : formatDate(annualData.reviewDate, 'dd MMM yyyy', 'en-US');
        } if (params.column.getColId() === 'paymentDeadline') {
          return annualData.paymentDeadline == null ? '' : formatDate(annualData.paymentDeadline, 'dd MMM yyyy', 'en-US');
        } if (params.column.getColId() === 'debt') {
          return currencyPipe.transform(annualData.debt, '$', 'code', '1.2-2') ?? '';
        } else if (params.column.getColId() === 'tags') {
          return (value as Tag[])?.map((tag: Tag) => tag.name)?.join(', ');
        } else if (params.column.getColId() === 'signingStatus') {
          return SigningStatus[ annualData.signingStatus ];
        }

        return value as string;
      },
      processHeaderCallback(params: ProcessHeaderForExportParams) {
        const colDef = params.column.getColDef() as ColumnWithExportName;
        return AnnualStatementsExportService.exportColumnNames[colDef.field ?? ''] ?? colDef.headerName;
      },
      sheetName,
      fileName: `${ sheetName }  ${ formatDate(new Date(), 'dd-MM-yyyy', 'en-US') }`
    }
  }
}
